<template>
  <div class="withdraw-page">
    <!-- 余额卡 -->
    <el-card class="balance-card" shadow="never">
      <div class="balance-head">
        <div>
          <div class="label">💰 可提现推广点</div>
          <div class="balance">{{ info.balance }} <span class="unit">点（1点=1元）</span></div>
        </div>
        <el-tag :type="info.frozen_days_ok ? 'success' : 'warning'" size="large">
          {{ info.frozen_days_ok ? '冻结期已过 ✅' : `冻结期：注册满 ${info.frozen_days_required} 天` }}
        </el-tag>
      </div>
      <el-descriptions :column="3" border class="balance-desc">
        <el-descriptions-item label="起提门槛">{{ info.min }} 点</el-descriptions-item>
        <el-descriptions-item label="本月已提">{{ info.month_used }} / {{ info.month_limit }} 元</el-descriptions-item>
        <el-descriptions-item label="本月剩余额度">{{ info.month_remain }} 元</el-descriptions-item>
      </el-descriptions>
      <div class="tips">
        <p>📌 提现规则：推广点满 {{ info.min }} 可提 · 注册满 {{ info.frozen_days_required }} 天解冻 · 每月限额 {{ info.month_limit }} 元 · 实名+人工审核（1-3 个工作日）</p>
        <p>⚠️ 充值点、体验点、会员积分不可提现，仅推广点（邀请奖励）可提现</p>
      </div>
    </el-card>

    <!-- 申请表单 -->
    <el-card class="apply-card" shadow="never">
      <template #header><span>💳 申请提现</span></template>
      <el-form :model="form" label-width="90px" style="max-width: 520px">
        <el-form-item label="提现金额">
          <el-input-number v-model="form.amount" :min="1" :max="info.balance" :precision="0" />
          <span class="form-hint">元（1 点 = 1 元，可提 {{ info.balance }} 点）</span>
        </el-form-item>
        <el-form-item label="真实姓名">
          <el-input v-model="form.real_name" placeholder="与身份证一致（实名认证）" maxlength="20" />
        </el-form-item>
        <el-form-item label="身份证号">
          <el-input v-model="form.id_card" placeholder="用于实名认证，仅审核可见" maxlength="18" />
        </el-form-item>
        <el-form-item label="收款方式">
          <el-radio-group v-model="form.pay_type">
            <el-radio value="alipay">支付宝</el-radio>
            <el-radio value="wechat">微信</el-radio>
            <el-radio value="bank">银行卡</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="form.pay_type === 'bank' ? '银行卡号' : '收款账号'">
          <el-input v-model="form.pay_account" :placeholder="form.pay_type === 'bank' ? '持卡人姓名 + 卡号' : '请输入收款账号'" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="submit">提交提现申请</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 提现记录 -->
    <el-card shadow="never">
      <template #header><span>📋 我的提现记录</span></template>
      <el-table :data="records" size="small" empty-text="暂无提现记录">
        <el-table-column prop="id" label="单号" width="70" />
        <el-table-column prop="amount" label="金额(元)" width="90" />
        <el-table-column label="状态" width="110">
          <template #default="{ row }">
            <el-tag :type="statusType(row.status)">{{ statusText(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="pay_type" label="方式" width="90">
          <template #default="{ row }">{{ payTypeText(row.pay_type) }}</template>
        </el-table-column>
        <el-table-column prop="pay_account" label="收款账号" min-width="140" />
        <el-table-column prop="applied_at" label="申请时间" width="150" />
        <el-table-column prop="reject_reason" label="驳回原因" min-width="140" />
      </el-table>
    </el-card>
  </div>
</template>

<script>
import api from '../api'

export default {
  name: 'Withdraw',
  data() {
    return {
      userId: Number(localStorage.getItem('user_id') || 0),
      info: { balance: 0, min: 100, month_limit: 500, month_used: 0, month_remain: 500, frozen_days_ok: false, frozen_days_required: 30 },
      form: { amount: 100, real_name: '', id_card: '', pay_type: 'alipay', pay_account: '' },
      records: [],
      submitting: false
    }
  },
  created() { this.load() },
  methods: {
    async load() {
      try {
        const [info, rec] = await Promise.all([
          api.get('/withdrawals/balance', { params: { user_id: this.userId } }),
          api.get('/withdrawals', { params: { user_id: this.userId } })
        ])
        this.info = info
        this.form.real_name = info.real_name || ''
        this.form.id_card = info.id_card || ''
        this.records = rec.records || []
      } catch (e) { this.$message.error(e.message) }
    },
    async submit() {
      if (!this.form.real_name || !this.form.id_card) return this.$message.warning('请填写真实姓名和身份证号')
      if (!this.form.pay_account) return this.$message.warning('请填写收款账号')
      this.submitting = true
      try {
        const r = await api.post('/withdrawals', { ...this.form, user_id: this.userId })
        this.$message.success(r.msg)
        this.load()
      } catch (e) { this.$message.error(e.message) } finally { this.submitting = false }
    },
    statusText(s) {
      return { pending: '审核中', approved: '待打款', rejected: '已驳回', paid: '已打款' }[s] || s
    },
    statusType(s) {
      return { pending: 'warning', approved: 'primary', rejected: 'danger', paid: 'success' }[s] || 'info'
    },
    payTypeText(t) {
      return { alipay: '支付宝', wechat: '微信', bank: '银行卡' }[t] || t
    }
  }
}
</script>

<style scoped>
.withdraw-page { display: flex; flex-direction: column; gap: 16px; }
.balance-card .balance-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.balance-card .label { color: #8b949e; font-size: 13px; }
.balance-card .balance { font-size: 34px; font-weight: 700; color: #10b981; margin-top: 4px; }
.balance-card .unit { font-size: 13px; color: #8b949e; font-weight: 400; }
.balance-desc { margin-bottom: 12px; }
.tips { color: #8b949e; font-size: 12px; line-height: 1.8; }
.tips p { margin: 0; }
.form-hint { color: #8b949e; font-size: 12px; margin-left: 10px; }
</style>
